/**
 * 1.6.1 Regenerate proxy (administrators only, Mikael 2026-10-26): re-make the proxy, poster, scrub images (with shot
 * detection when it is on) and waveform of the current version with the current settings (POST
 * /api/v1/assets/:id/process; the API answers 403 to others when the asset has a proxy).
 *
 *  - RegenerateProxyButton: the asset page's action bar (OverflowBar, low priority: it sits in More).
 *  - RegenerateProxyPanel: the Processing tab's line, with the same action.
 *  - RegenerateProxySelectionButton: the Library selection bar's, within BULK_ACTION_LIMIT
 *    (POST /api/v1/assets/regenerate-proxies).
 *
 * "No proxy for this asset → Generate proxy" (Asset.tsx NoProxy) keeps processing.retry.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { BULK_ACTION_LIMIT, tooManyAssetsMessage, type AssetDetail, type AssetSummary } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useMe } from '../api/hooks';
import { useAssetAction } from '../api/hooks/assets';
import { queryKeys } from '../api/queryKeys';
import { toast } from './Toasts';

export const REGENERATE_CONFIRM = 'Regenerate the proxy, poster and scrub images with the current settings?';

function useIsAdmin() {
  return Boolean(useMe().data?.permissions.includes('admin'));
}

/** Why Regenerate proxy can't run now (a reason), null when it can, undefined when it is not offered. */
export function regenerateBlocked(asset: AssetDetail): string | null | undefined {
  if (asset.deleted || asset.mediaKind === 'graphic' || asset.status === 'placeholder') return undefined;
  if (!asset.representations.some((r) => r.kind === 'proxy')) return undefined; // Generate proxy covers that
  if (asset.live && (asset.live.state === 'recording' || asset.live.state === 'finishing')) return 'The recording has not ended yet';
  if (asset.runs.some((r) => r.status === 'queued' || r.status === 'running')) return 'Processing is running';
  return null;
}

function useRegenerate(asset: AssetDetail) {
  const process = useAssetAction('process');
  return {
    pending: process.isPending,
    start: () => {
      if (!window.confirm(REGENERATE_CONFIRM)) return;
      process.mutate(asset.id, { onSuccess: () => toast('Regenerating the proxy'), onError: (e) => toast(errorMessage(e), 'error') });
    },
  };
}

export function RegenerateProxyButton({ asset }: { asset: AssetDetail }) {
  const admin = useIsAdmin();
  const r = useRegenerate(asset);
  const blocked = regenerateBlocked(asset);
  if (!admin || blocked === undefined) return null;
  return (
    <button className="btn sm" disabled={Boolean(blocked) || r.pending} title={blocked ?? 'Re-make the proxy, poster, scrub images and waveform with the current settings'} onClick={r.start} data-testid="regenerate-proxy">
      {r.pending ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} <span className="btn-label">Regenerate proxy</span>
    </button>
  );
}

export function RegenerateProxyPanel({ asset }: { asset: AssetDetail }) {
  const admin = useIsAdmin();
  const r = useRegenerate(asset);
  const blocked = regenerateBlocked(asset);
  if (!admin || blocked === undefined) return null;
  const proxy = asset.representations.find((x) => x.kind === 'proxy');
  return (
    <div className="shots-panel" data-testid="regenerate-panel">
      <RefreshCw size={14} className="muted" />
      <span className="muted">Proxy{proxy?.sizeBytes ? ` · ${(proxy.sizeBytes / 1e6).toFixed(1)} MB` : ''}</span>
      <span style={{ flex: 1 }} />
      <button className="btn sm" disabled={Boolean(blocked) || r.pending} title={blocked ?? undefined} onClick={r.start} data-testid="regenerate-proxy-panel">
        Regenerate proxy
      </button>
    </div>
  );
}

export function RegenerateProxySelectionButton({ selected }: { selected: AssetSummary[] }) {
  const admin = useIsAdmin();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (!admin) return null;
  const eligible = selected.filter((a) => (a.status === 'ready' || a.status === 'partial' || a.status === 'failed') && !a.deleted && a.mediaKind !== 'graphic');
  const n = eligible.length;
  const total = selected.length;
  const label = `Regenerate proxy on ${n === total ? total : `${n} of ${total}`}`;
  const blocked = n === 0 ? 'None of the selected assets has been processed' : n > BULK_ACTION_LIMIT ? `${tooManyAssetsMessage()} (${n} selected)` : null;

  async function run() {
    if (!window.confirm(`${REGENERATE_CONFIRM} (${n} asset${n === 1 ? '' : 's'})`)) return;
    setBusy(true);
    try {
      const r = await api<{ started: Array<{ assetId: string }>; skipped: Array<{ assetId: string; code: string; message: string }> }>(
        '/api/v1/assets/regenerate-proxies', { method: 'POST', json: { assetIds: eligible.map((a) => a.id) } });
      for (const a of r.started) void qc.invalidateQueries({ queryKey: queryKeys.asset(a.assetId) });
      void qc.invalidateQueries({ queryKey: queryKeys.assets() });
      const why = [...new Set(r.skipped.map((s) => s.message))].join(', ');
      toast(`Regenerating ${r.started.length === 1 ? '1 proxy' : `${r.started.length} proxies`}${r.skipped.length ? `; ${r.skipped.length} skipped (${why})` : ''}`, r.started.length ? undefined : 'error');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button className="btn ghost sm" disabled={busy || Boolean(blocked)} title={blocked ?? `${label} with the current settings`} onClick={() => void run()} data-testid="selection-regenerate" data-eligible={n}>
      {busy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} {label}
    </button>
  );
}
